import React, { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { ARROWS_KEY_CODES, CLICK_EVENT, DATE_PATTERN, KEYDOWN_EVENT, TODAY_TITLE } from './datepicker.consts';

import 'react-datepicker/dist/react-datepicker.css';
import './datepicker-overrides.less';

import { getMonthTitle, isDateValid } from './datepicker.helpers';

const Datepicker = props => {
  const { onChange, required, selected } = props;

  // Format date, avoid pass Invalid date or date string with format not supported by Safari
  const formatDate = (date) => {
    if (date instanceof Date) {
      if (Date.parse(date)) {
        return date;
      } else {
        return new Date();
      }
    }
    if (date && typeof date === "string") {
      return new Date(date.replace(/-/g, "/"));
    }
    return date;
  }

  const changeYearRef = useRef(() => {
  });
  const pickerRef = useRef(null);
  const pickerComponentRef = useRef(null);

  const [currentYear, setCurrentYear] = useState(
    (props.selected ? formatDate(props.selected) : new Date()).getFullYear()
  );

  const prevYear = currentYear - 1;
  const nextYear = currentYear + 1;

  const syncYear = (date) => {
    const year = date && date.getFullYear();
    if (currentYear !== year) {
      setCurrentYear(year);
    }
  };

  const changeIfNeeded = value => {
    if (value.getTime() !== new Date(selected).getTime()) {
      let date = null;

      if (isDateValid(value)) {
        date = value;
      } else if (required) {
        date = new Date();
      }

      onChange(date);
      syncYear(date);
    }
  };

  const addYearIfNeeded = value => {
    const match = [...value.matchAll(DATE_PATTERN)];
    const separator = match[0] && match[0][1];

    let date = new Date();

    if (match.length === 3) {
      date = new Date(value);
    }

    if (match.length === 2 && separator) {
      date = new Date(`${value}${separator}${date.getFullYear()}`);
    }

    return date;
  };

  const handleBlur = ({ target: { value } }) => changeIfNeeded(addYearIfNeeded(value));

  const handleChange = (date, event) => {
    if (event.type === CLICK_EVENT || event.type === KEYDOWN_EVENT) {
      changeIfNeeded(date);
    } else {
      if (pickerComponentRef.current) {
        const date = addYearIfNeeded(event.target.value);
        if (isDateValid(date)) {
          pickerComponentRef.current.setPreSelection(addYearIfNeeded(event.target.value));
          syncYear(date);
        }
      }
    }
  };

  const handleKeyDown = event => {
    if (ARROWS_KEY_CODES.includes(event.keyCode)) {
      event.stopPropagation();
    }
  };

  const handleOpen = () => {
    const picker = document.querySelector('.react-datepicker__tab-loop');
    pickerRef.current = picker && picker.previousSibling;
    if (pickerRef.current) {
      pickerRef.current.addEventListener('keydown', handleKeyDown, true);
    }
  };

  const handleClose = () => {
    if (pickerRef.current) {
      pickerRef.current.removeEventListener('keydown', handleKeyDown);
    }
  };

  const handleChangeYear = year => () => {
    setCurrentYear(year);
    changeYearRef.current(year);
  };

  const renderCustomHeader = ({
    date,
    changeYear,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled
  }) => {
    changeYearRef.current = changeYear;

    return (
      <div
        style={{
          margin: 10,
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <button
          type='button'
          className='btn btn-link icon chevron-left'
          onClick={() => {
            decreaseMonth();
            if (date.getMonth() === 0) {
              setCurrentYear(year => year - 1);
            }
          }}
          disabled={prevMonthButtonDisabled}
        />
        <div className='datepicker__month_title'>{getMonthTitle(date)}</div>
        <button
          type='button'
          className='btn btn-link icon chevron-right'
          onClick={() => {
            increaseMonth();
            if (date.getMonth() === 11) {
              setCurrentYear(year => year + 1);
            }
          }}
          disabled={nextMonthButtonDisabled}
        />
      </div>
    );
  };

  return (
    <DatePicker
      placeholderText='Select a date'
      {...props}
      selected={formatDate(props.selected)}
      minDate={props.minDate && formatDate(props.minDate)}
      maxDate={props.maxDate && formatDate(props.maxDate)}
      ref={pickerComponentRef}
      renderCustomHeader={renderCustomHeader}
      showPopperArrow={false}
      todayButton={TODAY_TITLE}
      onBlur={handleBlur}
      onChange={handleChange}
      onCalendarOpen={handleOpen}
      onCalendarClose={handleClose}
      popperClassName='datepicker__popover_container'
      wrapperClassName='datepicker__wrapper'
      calendarClassName='holdshort-datepicker'
      fixedHeight
    >
      <div className='datepicker__footer'>
        <div className='datepicker__footer_prev_year' onClick={handleChangeYear(prevYear)}>
          {prevYear}
        </div>
        <div className='datepicker__footer_current_year'>{currentYear}</div>
        <div className='datepicker__footer_next_year' onClick={handleChangeYear(nextYear)}>
          {nextYear}
        </div>
      </div>
    </DatePicker>
  );
};

export default Datepicker;
